/**
 * Utility functions for handling question-level background images
 * Supports desktop, mobile, and fallback to step-level backgrounds
 */

/**
 * Get the effective background image ID for a question
 * Uses only question-level image, does NOT fall back to step-level
 * @param {Object} question - Question object with optional questionBackgroundImage(Id)
 * @param {Object} stepBackground - Step-level background image object (ignored - kept for backwards compatibility)
 * @returns {string|null} - Image ID or null
 */
export const getQuestionBackgroundImageId = (question, stepBackground) => {
  return (
    question?.questionBackgroundImage?.id ||
    question?.questionBackgroundImageId ||
    null
  )
}

/**
 * Get the effective background image ID for mobile
 * Prefers question-level image, falls back to step-level
 * @param {Object} question - Question object with optional mobileQuestionBackgroundImage(Id)
 * @param {Object} stepMobileBackground - Step-level mobile background image object
 * @returns {string|null} - Image ID or null
 */
export const getMobileQuestionBackgroundImageId = (question, stepMobileBackground) => {
  return (
    question?.mobileQuestionBackgroundImage?.id ||
    question?.mobileQuestionBackgroundImageId ||
    stepMobileBackground?.id ||
    null
  )
}

/**
 * Get the alt text for a question background image
 * Extracts from question-level image only, does NOT fall back to step-level
 * @param {Object} question - Question object with optional questionBackgroundImage
 * @param {Object} stepBackground - Step-level background image object (ignored - kept for backwards compatibility)
 * @returns {string} - Alt text or empty string
 */
export const getQuestionBackgroundImageAlt = (question, stepBackground) => {
  return (
    question?.questionBackgroundImage?.altAttribute ||
    ''
  )
}

/**
 * Get the alt text for a question mobile background image
 * @param {Object} question - Question object with optional mobileQuestionBackgroundImage
 * @param {Object} stepMobileBackground - Step-level mobile background image object
 * @returns {string} - Alt text or empty string
 */
export const getMobileQuestionBackgroundImageAlt = (question, stepMobileBackground) => {
  return (
    question?.mobileQuestionBackgroundImage?.altAttribute ||
    stepMobileBackground?.altAttribute ||
    ''
  )
}

/**
 * Get the background color for a question
 * Uses question-level color, with sensible default dark background for quiz cards
 * Does NOT fall back to step-level color to maintain quiz dark appearance
 * @param {Object} question - Question object with optional questionBackgroundColor
 * @param {string} stepBackgroundColor - Step-level background color (ignored for quiz integrity)
 * @returns {string} - Hex color or dark default
 */
export const getQuestionBackgroundColor = (question, stepBackgroundColor) => {
  // Always use question's color if defined, otherwise use a dark default (#1a1a1a)
  // Do NOT use stepBackgroundColor to maintain quiz's dark card appearance
  return question?.questionBackgroundColor || '#1a1a1a'
}

/**
 * Log debug information about question background images
 * @param {Object} question - Question object
 * @param {number} position - Question position (1-based)
 * @param {Object} stepBackground - Step-level background
 */
export const debugLogQuestionBackground = (question, position, stepBackground) => {
  if (false) {
    const questionBackgroundImageId = getQuestionBackgroundImageId(question, stepBackground)
    const mobileBackgroundImageId = getMobileQuestionBackgroundImageId(question, null)
    const alt = getQuestionBackgroundImageAlt(question, stepBackground)
    const mobileAlt = getMobileQuestionBackgroundImageAlt(question, null)

    console.log(`[PersonalityQuiz Question ${position}] Background Config:`, {
      questionId: question.id,
      questionText: question.text.substring(0, 50) + '...',
      useQuestionBackground: !!questionBackgroundImageId,
      useDefaultImage: question.useDefaultImage,
      questionBackgroundImageId,
      mobileBackgroundImageId,
      backgroundImageAlt: alt,
      mobileBackgroundImageAlt: mobileAlt,
      fallbackToStep: !question.questionBackgroundImage && !!stepBackground,
    })
  }
}
